// features/tutorials/hooks/useLearningProgress.ts
import { useState, useEffect, useCallback } from 'react';
import { tutorialModules } from '../data/tutorialContent';
import type { LearningProgress, TutorialLesson, TutorialModule } from '../types';

const defaultProgress: LearningProgress = {
    completedLessons: [],
    completedSteps: {},
    lastViewedLesson: null,
    lastViewedStep: {},
    totalTimeSpent: 0,
    streakDays: 0,
    lastActivityDate: '',
    modulesCompleted: [],
};

const STORAGE_KEY = 'etipos_learning_progress';

export const useLearningProgress = () => {
    const [progress, setProgress] = useState<LearningProgress>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                // Validate the parsed data has the correct structure
                return {
                    ...defaultProgress,
                    ...parsed,
                };
            }
            return defaultProgress;
        } catch (error) {
            console.error('Failed to load learning progress:', error);
            return defaultProgress;
        }
    });

    // Save to localStorage whenever progress changes
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
        } catch (error) {
            console.error('Failed to save progress:', error);
            // Could show a toast notification here
        }
    }, [progress]);

    // Calculate streak
    useEffect(() => {
        const today = new Date().toISOString().split('T')[0];
        const lastActivity = progress.lastActivityDate;

        if (lastActivity !== today) {
            const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

            setProgress((prev: LearningProgress) => ({
                ...prev,
                streakDays: lastActivity === yesterday ? prev.streakDays + 1 : 1,
                lastActivityDate: today,
            }));
        }
    }, []); // Run only on mount

    const completeLesson = useCallback((lessonId: string) => {
        setProgress((prev: LearningProgress) => {
            if (prev.completedLessons.includes(lessonId)) return prev;

            const now = new Date().toISOString().split('T')[0];
            return {
                ...prev,
                completedLessons: [...prev.completedLessons, lessonId],
                lastViewedLesson: lessonId,
                lastActivityDate: now,
            };
        });
    }, []);

    const completeModule = useCallback((moduleId: string) => {
        setProgress((prev: LearningProgress) => {
            if (prev.modulesCompleted.includes(moduleId)) return prev;

            return {
                ...prev,
                modulesCompleted: [...prev.modulesCompleted, moduleId],
            };
        });
    }, []);

    const completeStep = useCallback((lessonId: string, stepIndex: number) => {
        setProgress((prev: LearningProgress) => {
            const lessonSteps = prev.completedSteps[lessonId] || [];
            if (lessonSteps.includes(stepIndex)) return prev;

            const now = new Date().toISOString().split('T')[0];
            return {
                ...prev,
                completedSteps: {
                    ...prev.completedSteps,
                    [lessonId]: [...lessonSteps, stepIndex],
                },
                lastViewedStep: {
                    ...prev.lastViewedStep,
                    [lessonId]: stepIndex,
                },
                lastActivityDate: now,
            };
        });
    }, []);

    const isLessonCompleted = useCallback((lessonId: string): boolean => {
        return progress.completedLessons.includes(lessonId);
    }, [progress.completedLessons]);

    const isModuleCompleted = useCallback((moduleId: string): boolean => {
        return progress.modulesCompleted.includes(moduleId);
    }, [progress.modulesCompleted]);

    const isStepCompleted = useCallback((lessonId: string, stepIndex: number): boolean => {
        const lessonSteps = progress.completedSteps[lessonId] || [];
        return lessonSteps.includes(stepIndex);
    }, [progress.completedSteps]);

    const getModuleProgress = useCallback((moduleId: string): number => {
        const module = tutorialModules.find((m: TutorialModule) => m.id === moduleId);
        if (!module || module.lessons.length === 0) return 0;

        const completedCount = module.lessons.filter((l: TutorialLesson) =>
            progress.completedLessons.includes(l.id)
        ).length;

        return Math.round((completedCount / module.lessons.length) * 100);
    }, [progress.completedLessons]);

    const getOverallProgress = useCallback((): number => {
        const totalLessons = tutorialModules.reduce((acc: number, m: TutorialModule) => acc + m.lessons.length, 0);
        if (totalLessons === 0) return 0;

        const completedCount = progress.completedLessons.length;
        return Math.round((completedCount / totalLessons) * 100);
    }, [progress.completedLessons]);

    const getResumeLearning = useCallback(() => {
        if (!progress.lastViewedLesson) return null;

        for (const module of tutorialModules) {
            const lesson = module.lessons.find((l: TutorialLesson) => l.id === progress.lastViewedLesson);
            if (lesson) return { module, lesson };
        }
        return null;
    }, [progress.lastViewedLesson]);

    const resetProgress = useCallback(() => {
        setProgress(defaultProgress);
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    return {
        progress,
        completeLesson,
        completeModule,
        completeStep,
        isLessonCompleted,
        isModuleCompleted,
        isStepCompleted,
        getModuleProgress,
        getOverallProgress,
        getResumeLearning,
        resetProgress,
    };
};